// src/context/AuthContext.js
// ─────────────────────────────────────────────────────────────────────────────
// Single source of truth for the current user and role flags.
// All components read from here instead of each calling /api/me/ independently.
// ─────────────────────────────────────────────────────────────────────────────
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../axiosConfig";

const AuthContext = createContext(null);

// ── Role normaliser (same logic used everywhere in the app) ──────────────────
const norm = (g) => String(g).trim().toLowerCase().replace(/[\s_]+/g, "-");

function deriveFlags(user) {
  if (!user) return { isAdmin: false, isAgencyFP: false, isCountryFP: false, isOfficeFP: false, canManageUsers: false };
  const groups = (Array.isArray(user.groups) ? user.groups : []).map(norm);
  const isAdmin     = !!user.is_superuser || groups.includes("ghg-admin");
  const isAgencyFP  = groups.includes("ghg-agency-focal-point");
  const isCountryFP = groups.includes("ghg-country-focal-point");
  const isOfficeFP  = groups.includes("ghg-office-focal-point");
  const canManageUsers = isAdmin || isAgencyFP || isCountryFP;
  return { isAdmin, isAgencyFP, isCountryFP, isOfficeFP, canManageUsers };
}

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true); // true while the initial /api/me/ is in-flight

  // ── Fetch current user ────────────────────────────────────────────────────
  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/api/me/");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Ensure CSRF cookie is set before any authenticated requests
    api.get("/api/auth/csrf/").finally(() => fetchMe());
  }, [fetchMe]);

  // ── Login ─────────────────────────────────────────────────────────────────
  // 1. Fetch CSRF cookie (Django sets csrftoken cookie on GET)
  // 2. POST credentials — Django sets the HttpOnly session cookie on success
  // 3. Re-fetch /api/me/ to populate user state
  const login = useCallback(async (username, password) => {
    await api.get("/api/auth/csrf/");
    const { data } = await api.post("/api/auth/login/", { username, password });
    await fetchMe();
    return data;
  }, [fetchMe]);

  // ── Logout ────────────────────────────────────────────────────────────────
  const logout = useCallback(async () => {
    try { await api.post("/api/auth/logout/"); } catch { /* ignore */ }
    setUser(null);
  }, []);

  const flags = deriveFlags(user);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      ...flags,
      login,
      logout,
      refetch: fetchMe,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

// ── Convenience hook ──────────────────────────────────────────────────────────
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}